/**
 * Tool-part input normalization — the ONE place that knows a UIMessage tool
 * part's arguments can be missing, partial, or not an object at all.
 *
 * Why this exists (prod incident 2026-07-29): when the model's tool-call JSON
 * fails to parse, the AI SDK does NOT drop the part. `processUIMessageStream`
 * writes a part shaped like:
 *
 *   {
 *     type: 'tool-callTool',
 *     state: 'output-error',
 *     input: undefined,                                   // <- never set
 *     rawInput: '{"name": "sandbox.write-file", "input": ', // <- raw string
 *     errorText: 'Invalid input for tool callTool: JSON parsing failed…',
 *   }
 *
 * (see the `tool-input-error` case in ai/dist — `input: void 0, rawInput:
 * chunk.input`). Every renderer/effect that reads `part.input` therefore has to
 * survive `undefined`, and should prefer `rawInput` when it is the only thing
 * carrying the model's intent. `state: 'output-error'` parts also reach the
 * renderers with NO `output` at all — the failure lives in `errorText`.
 *
 * Pure + framework-agnostic so the incident shape is trivially unit-testable.
 */

export interface ToolPartLike {
  type?: string;
  state?: string;
  input?: unknown;
  /** Set by the AI SDK when the model's tool-call arguments could not be parsed. */
  rawInput?: unknown;
  output?: unknown;
  errorText?: string;
  toolCallId?: string;
}

/** True for a plain, non-array object. */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Accept ANY shape the call sites have on hand (a typed UIMessage part, a
 * `Record<string, unknown>` row out of a persisted transcript, null, …) without
 * forcing each one to cast. Everything below re-checks each field it reads.
 */
function asPart(value: unknown): ToolPartLike | undefined {
  return isPlainObject(value) ? (value as ToolPartLike) : undefined;
}

/**
 * Best-effort object view of a tool part's arguments.
 *
 * Order: a usable `input` object wins; otherwise fall back to `rawInput`,
 * JSON-parsing it when it is a string (a TRUNCATED string simply fails to parse
 * and yields `undefined` — never a throw). Returns `undefined` when nothing
 * object-shaped is available, which every call site already treats as "no
 * input".
 */
export function readToolPartInput(rawPart: unknown): Record<string, unknown> | undefined {
  const part = asPart(rawPart);
  if (!part) return undefined;
  if (isPlainObject(part.input)) return part.input;
  const raw = part.rawInput;
  if (isPlainObject(raw)) return raw;
  if (typeof raw === 'string' && raw.length > 0) {
    try {
      const parsed: unknown = JSON.parse(raw);
      if (isPlainObject(parsed)) return parsed;
    } catch {
      // Truncated / non-JSON payload — there is no object to show.
    }
  }
  return undefined;
}

/**
 * The raw arguments text to SHOW the user for a failed call, when there is no
 * parsed input. Bounded so a huge truncated payload can't blow out a card.
 */
export function readToolPartRawInputText(
  rawPart: unknown,
  maxChars = 600,
): string | undefined {
  const part = asPart(rawPart);
  if (!part) return undefined;
  const raw = part.rawInput;
  let text: string | undefined;
  if (typeof raw === 'string') {
    text = raw;
  } else if (raw !== undefined && raw !== null) {
    try {
      text = JSON.stringify(raw, null, 2);
    } catch {
      text = undefined;
    }
  }
  if (!text || !text.trim()) return undefined;
  return text.length > maxChars ? `${text.slice(0, maxChars)}…` : text;
}

/** True when the SDK marked this part as a failed tool call. */
export function isFailedToolPart(rawPart: unknown): boolean {
  const part = asPart(rawPart);
  if (!part) return false;
  if (part.state === 'output-error') return true;
  const output = part.output;
  return isPlainObject(output) && output.success === false;
}

/**
 * One user-facing failure line for an `output-error` part. Prefers the tool's
 * own structured `output.error`, then the SDK's `errorText`, then a generic
 * line — NEVER an empty string (a blank error card reads as a UI bug).
 */
export function readToolPartErrorText(rawPart: unknown): string {
  const part = asPart(rawPart);
  const output = part?.output;
  if (isPlainObject(output) && typeof output.error === 'string' && output.error.trim()) {
    return output.error;
  }
  if (typeof part?.errorText === 'string' && part.errorText.trim()) {
    return part.errorText;
  }
  return 'The tool call failed before it could run.';
}
